import { useEffect, useState } from "react";
import { toast } from "sonner";

// Installing Observer as an app: the service worker that makes it installable, and the install prompt Android
// hands us (iOS has no prompt; Safari's Share → Add to Home Screen is the only way, so we show the steps).

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // Chrome's own mini-bar is replaced by our button
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export const isStandalone = () =>
  (typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true)) || false;

export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isAndroid = () => /android/i.test(navigator.userAgent);

export function registerServiceWorker() {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      // A new build was deployed: once its worker is ready, offer a reload rather than switching under a running meeting.
      reg.addEventListener("updatefound", () => {
        const w = reg.installing;
        w?.addEventListener("statechange", () => {
          if (w.state === "installed" && navigator.serviceWorker.controller) {
            toast("Observer has been updated", {
              description: "Reload to get the new version. Finish a running meeting first.",
              action: { label: "Reload", onClick: () => { w.postMessage("SKIP_WAITING"); window.location.reload(); } },
              duration: 15000,
            });
          }
        });
      });
    }).catch(() => {});
  });
}

// What the UI needs: whether there is a prompt to show (Android Chrome and desktop Chrome/Edge), whether we are
// already running as an app, and whether this is iOS (manual steps).
export function useInstall() {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  const prompt = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") deferredPrompt = null;
    notify();
    return outcome === "accepted";
  };
  return { canPrompt: !!deferredPrompt, standalone: isStandalone(), ios: isIOS(), android: isAndroid(), prompt };
}
